import { createContext, createElement, useContext, useEffect, useState, type ReactNode } from 'react'

/**
 * How close an hour label may sit to the now-pill before it is hidden.
 *
 * Forty minutes is the dispatch mockup's own threshold: the pill is the time
 * itself, and an hour mark under it is noise.
 */
export const NOW_LABEL_HIDE_MINUTES = 40

/**
 * Where we are in the day.
 *
 * Ticks once a minute rather than once a second: the line is for orientation,
 * and a seconds-accurate marker on a fifteen-minute grid is false precision.
 */
export function useNow(): Date {
  const [now, setNow] = useState(() => new Date())

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000)
    return () => clearInterval(timer)
  }, [])

  return now
}

const BoardClockContext = createContext<Date | null>(null)

/**
 * One clock for the whole board. Every booking reads the time to say whether
 * it is on site, running over or done, and forty timers ticking a minute
 * apart would let neighbours disagree for a few seconds.
 */
export function BoardClockProvider({ children }: { children: ReactNode }) {
  const now = useNow()
  return createElement(BoardClockContext.Provider, { value: now }, children)
}

/** The board's clock, or the time now where there is no board around it. */
export function useBoardClock(): Date {
  return useContext(BoardClockContext) ?? new Date()
}

export function nowInWindow(now: Date, windowStart: Date, windowEnd: Date): boolean {
  return now >= windowStart && now <= windowEnd
}
